import { useEffect, useMemo, useState } from "react";
import {
    CircleNotchIcon,
    DownloadSimpleIcon,
    SparkleIcon,
    ArrowClockwiseIcon,
    LockSimpleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { track } from "@/lib/analytics";
import { downloadImage } from "@/lib/downloadImage";
import BuyCreditsCard from "@/components/BuyCreditsCard";

const POLL_MS = 2500;
const MAX_POLLS = 80;

export default function ScenesPanel({
    editedImageUrl,
    editId,
    balance,
    onBalanceChange,
}) {
    const [scenes, setScenes] = useState([]);
    const [state, setState] = useState({});
    const locked = balance <= 0;

    useEffect(() => {
        api.get("/scenes")
            .then((r) => setScenes(r.data))
            .catch(() => toast.error("Couldn't load scenes."));
    }, []);

    const setSceneState = (id, partial) =>
        setState((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...partial } }));

    const pollUntilDone = async (jobId) => {
        for (let i = 0; i < MAX_POLLS; i++) {
            await new Promise((r) => setTimeout(r, POLL_MS));
            try {
                const { data } = await api.get(`/edit/${jobId}`, {
                    timeout: 90000,
                });
                if (data.status === "done") return data;
                if (data.status === "error") {
                    const e = new Error(
                        data.detail || "Scene generation failed.",
                    );
                    e.serverError = true;
                    throw e;
                }
            } catch (err) {
                if (err?.serverError) throw err;
                if (err?.response?.data?.detail) throw err;
                // transient (network blip); keep polling
            }
        }
        throw new Error("Scene generation timed out. Please try again.");
    };

    const generate = async (scene) => {
        if (!editedImageUrl || locked) return;
        setSceneState(scene.id, { status: "pending", resultUrl: null });
        track("scene_generate_start", { scene_id: scene.id });

        try {
            const res = await fetch(editedImageUrl);
            const portraitBlob = await res.blob();
            const form = new FormData();
            form.append("file", portraitBlob, "portrait.png");
            form.append("scene_id", scene.id);

            const { data: started } = await api.post(
                "/scenes/generate",
                form,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 180000,
                },
            );
            onBalanceChange?.();
            setSceneState(scene.id, { jobId: started.job_id });

            try {
                const result = await pollUntilDone(started.job_id);
                const resultUrl = `data:${result.mime};base64,${result.image_b64}`;
                setSceneState(scene.id, { status: "done", resultUrl });
                onBalanceChange?.();
                track("scene_generate_success", { scene_id: scene.id });
            } catch (inner) {
                onBalanceChange?.();
                throw inner;
            }
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.message ||
                "Scene generation failed.";
            setSceneState(scene.id, { status: "error", error: msg });
            toast.error(msg);
            track("scene_generate_error", {
                scene_id: scene.id,
                message: msg,
            });
            if (err?.response?.status === 402) onBalanceChange?.();
        }
    };

    const downloadScene = async (scene) => {
        const s = state[scene.id];
        if (!s?.resultUrl) return;
        try {
            const result = await downloadImage(
                s.resultUrl,
                `haogeili-${scene.id}-${editId || "scene"}.png`,
            );
            track("scene_download", {
                scene_id: scene.id,
                method: result.method,
                ok: result.ok,
            });
            if (!result.ok) {
                toast.error("下载失败，请稍后再试 / Download failed.");
            } else if (result.method === "new_tab") {
                toast(
                    "长按图片保存到相册 / Long-press the image to save.",
                    { duration: 6000 },
                );
            }
        } catch {
            toast.error("下载失败，请稍后再试 / Download failed.");
        }
    };

    const generateAll = () => {
        if (locked) return;
        scenes.forEach((s) => {
            const current = state[s.id]?.status;
            if (current !== "pending" && current !== "done") generate(s);
        });
    };

    const anyPending = useMemo(
        () => Object.values(state).some((s) => s?.status === "pending"),
        [state],
    );
    const allDone = useMemo(
        () =>
            scenes.length > 0 &&
            scenes.every((s) => state[s.id]?.status === "done"),
        [scenes, state],
    );

    return (
        <section data-testid="scenes-panel" className="pt-4">
            {locked ? (
                <div className="mb-5">
                    <BuyCreditsCard source="scenes_panel" />
                </div>
            ) : null}

            {!locked && scenes.length > 0 && !allDone ? (
                <div className="flex justify-end mb-4">
                    <button
                        type="button"
                        onClick={generateAll}
                        disabled={anyPending}
                        data-testid="scenes-generate-all"
                        className={[
                            "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-all",
                            anyPending
                                ? "bg-white/10 text-white/40 cursor-not-allowed"
                                : "bg-white text-black hover:bg-white/90 active:scale-[0.99]",
                        ].join(" ")}
                    >
                        <SparkleIcon size={13} weight="fill" />
                        Generate all
                    </button>
                </div>
            ) : null}

            <div className="flex justify-center">
                <div className="grid grid-cols-1 gap-4 w-full max-w-md">
                {scenes.map((scene) => {
                    const s = state[scene.id] || {};
                    const previewUrl = s.resultUrl
                        ? s.resultUrl
                        : `${API}${scene.template_url}`;
                    return (
                        <div
                            key={scene.id}
                            data-testid={`scene-card-${scene.id}`}
                            className={[
                                "group relative rounded-3xl overflow-hidden bg-[#141417] border transition-all",
                                locked
                                    ? "border-white/5"
                                    : "border-white/5 hover:border-white/10",
                            ].join(" ")}
                        >
                            <div className="relative aspect-square bg-black overflow-hidden">
                                <img
                                    src={previewUrl}
                                    alt={scene.name}
                                    className={[
                                        "absolute inset-0 w-full h-full object-cover transition-all duration-500",
                                        s.resultUrl
                                            ? "opacity-100"
                                            : locked
                                              ? "opacity-25 blur-[2px] grayscale"
                                              : "opacity-40 grayscale",
                                    ].join(" ")}
                                    draggable={false}
                                />
                                {s.status === "pending" ? (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50 backdrop-blur-[2px]">
                                        <CircleNotchIcon
                                            size={28}
                                            weight="bold"
                                            className="animate-spin text-white"
                                        />
                                        <p className="text-xs text-white/80 tracking-wide">
                                            Generating scene…
                                        </p>
                                    </div>
                                ) : null}
                                {!s.resultUrl && s.status !== "pending" ? (
                                    <div className="absolute inset-x-0 top-3 flex justify-center pointer-events-none">
                                        {locked ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold text-white/80 bg-black/70 backdrop-blur-sm border border-white/15 rounded-full px-2.5 py-1">
                                                <LockSimpleIcon
                                                    size={11}
                                                    weight="bold"
                                                />
                                                Locked
                                            </span>
                                        ) : (
                                            <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-white/70 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full px-2.5 py-1">
                                                Preview
                                            </span>
                                        )}
                                    </div>
                                ) : null}
                                {s.resultUrl ? (
                                    <span className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase font-semibold text-black bg-white rounded-full px-2.5 py-1">
                                        You
                                    </span>
                                ) : null}
                            </div>

                            <div className="p-4 space-y-3">
                                <div>
                                    <h3 className="font-heading text-base font-medium">
                                        {scene.name}
                                    </h3>
                                    <p className="text-xs text-white/50 mt-0.5 leading-relaxed">
                                        {scene.description}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {s.status === "done" ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    downloadScene(scene)
                                                }
                                                data-testid={`scene-download-${scene.id}`}
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full py-2.5 bg-white text-black text-xs font-medium hover:bg-white/90 active:scale-[0.99] transition-all"
                                            >
                                                <DownloadSimpleIcon
                                                    size={13}
                                                    weight="bold"
                                                />
                                                Download
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => generate(scene)}
                                                disabled={locked}
                                                data-testid={`scene-regen-${scene.id}`}
                                                className="inline-flex items-center justify-center rounded-full w-9 h-9 bg-[#222226] text-white hover:bg-[#2A2A2F] border border-white/5 transition-all disabled:opacity-40"
                                                title={
                                                    locked
                                                        ? "Out of credits"
                                                        : "Regenerate"
                                                }
                                            >
                                                <ArrowClockwiseIcon
                                                    size={13}
                                                    weight="bold"
                                                />
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => generate(scene)}
                                            disabled={
                                                locked ||
                                                s.status === "pending"
                                            }
                                            data-testid={`scene-generate-${scene.id}`}
                                            className={[
                                                "flex-1 inline-flex items-center justify-center gap-1.5 rounded-full py-2.5 text-xs font-medium transition-all",
                                                locked ||
                                                s.status === "pending"
                                                    ? "bg-white/10 text-white/40 cursor-not-allowed"
                                                    : "bg-white text-black hover:bg-white/90 active:scale-[0.99]",
                                            ].join(" ")}
                                        >
                                            {s.status === "pending" ? (
                                                <>
                                                    <CircleNotchIcon
                                                        size={13}
                                                        weight="bold"
                                                        className="animate-spin"
                                                    />
                                                    Working…
                                                </>
                                            ) : locked ? (
                                                <>
                                                    <LockSimpleIcon
                                                        size={13}
                                                        weight="bold"
                                                    />
                                                    Out of credits
                                                </>
                                            ) : (
                                                <>
                                                    <SparkleIcon
                                                        size={13}
                                                        weight="fill"
                                                    />
                                                    Generate · 1 credit
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                </div>
            </div>
        </section>
    );
}
